"use client";

//region Imports
import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {useForm, useWatch} from "react-hook-form";
import _ from "lodash";

import {$core, HttpStatus} from "@isudio/core";
import {$store} from '@isudio/globals';
import {pages, policies} from "@isudio/config";
import {getCountries, useMergeRefs} from "@isudio/utils";
import type {GenderType, IUserPayload, IUserProfile, IUserSignupResponse} from "@isudio/data";
import {AccountTypes} from "@isudio/data";
import type {FooterElement, HeaderElement, BaseDialogProps} from "../";
import {LoadingSpinner, MediaPicker, RowGrid, Confirm, ToolbarTop, ToolbarBottom, GenderRadio, InputValidator, DatePicker, PhoneInput, useDialogController, useTippyRegistry} from "../";
//endregion imports

//region Types & Interfaces
interface SignupProps extends BaseDialogProps {
  dialogOptions?: any;
  accountType?: AccountTypes;
  email?: string;
  organization?: string | undefined;
  institution?: string | undefined;
  onSuccess?: () => void;
  onClose?: () => void;
}
//endregion Types & Interfaces

export function Signup({
                         dialogOptions,
                         accountType,
                         email,
                         organization,
                         institution,
                         onSuccess,
                         onClose
                       }: SignupProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");

  const isAdmin = accountType === AccountTypes.admin && !_.isNothing(email);
  const isBizAdmin = accountType === AccountTypes.bizAdmin && !_.isNothing(email);
  const isStaff = accountType === AccountTypes.staff && !_.isNothing(email);
  const isStudent = accountType === AccountTypes.student && !_.isNothing(email);
  const isUserInvite = accountType === AccountTypes.user && !_.isNothing(email);
  const isUser = !isAdmin && !isBizAdmin && !isStaff && !isStudent && !isUserInvite;

  const defaultProfilePic = _.get(pages.signup.mediaPicker, _.isDark() ? "defaultProfilePic" : "defaultProfilePicLight");
  const [isUnsaved, setIsUnsaved] = useState<boolean>(false);
  const [photo, setPhoto] = useState<string | null>(defaultProfilePic);
  const [image, setImage] = useState<any | null>(defaultProfilePic);
  const [defaultImage, setDefaultImage] = useState<string>(defaultProfilePic);
  const [genderClass, setGenderClass] = useState<string>("gender-radio");
  const [asyncValidation, setAsyncValidation] = useState<{
    socialID?: true | string;
    email?: true | string;
  }>({});
  const [resetKey, setResetKey] = useState(0);

  const firstNameRef = useRef<HTMLInputElement | null>(null);
  const socialIDRef = useRef<HTMLInputElement | null>(null);
  const dobRef = useRef<HTMLButtonElement | null>(null);
  const genderRef = useRef<HTMLButtonElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const mobileRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedRef = useRef<any | null>(null);

  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  const transformCountry = (c: any) => {
    return {
      label: c.name.english,
      value: c.code.phone,
      code: c.code.short2.toLowerCase(),
      imageUrl: `/assets/fonts/flag-icons/flags/png/${c.code.short2.toLowerCase()}.png`
    };
  };
  const country = transformCountry(_.get($store.Device.data, "location.geoCode.country", $store.Data.countries$.getValue().default));
  const countries = getCountries().map((c: any) => {
    return {
      label: c.name,
      value: c.dialCode,
      code: c.code,
      imageUrl: c.imageUrl
    };
  });
  const defaults: IUserProfile = useMemo(() => ({
    accountType: accountType || AccountTypes.user,
    firstName: "",
    lastName: "",
    socialID: "",
    gender: "",
    dateOfBirth: "",
    email: email || "",
    country: undefined,
    mobile: "",
    organization: organization || "",
    designation: "",
    institution: institution || "",
    discipline: ""
  }), [accountType, email]);

  const {
    control,
    register,
    trigger,
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    setFocus,
    setError,
    clearErrors,
    formState: {
      isDirty,
      isValid,
      errors,
    }
  } = useForm<IUserProfile>({
    criteriaMode: "all",
    defaultValues: defaults
  });

  const firstName = useWatch({ control, name: "firstName" });
  const lastName = useWatch({ control, name: "lastName" });
  //endregion Variables

  //region Form Validations
  const validations: any = {
    firstName: {
      required: "First name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `First name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `First name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    lastName: {
      required: "Last name is required.",
      minLength: {
        value: userPolicy.name.minLength,
        message: `Last name should have at least ${userPolicy.name.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.name.maxLength,
        message: `Last name can't exceed ${userPolicy.name.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z ]*$/,
        message: "Invalid characters entered.",
      }
    },
    socialID: {
      required: "Social ID is required.",
      minLength: {
        value: userPolicy.socialID.minLength,
        message: `Social ID should have at least ${userPolicy.socialID.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.socialID.maxLength,
        message: `Social ID can't exceed ${userPolicy.socialID.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z][a-zA-Z0-9-@_.]*$/,
        message: "Invalid characters entered.",
      },
      valueExists: {
        message: "Social ID “{value}” has already signed up.\nPlease sign in if it is yours or use a different social ID."
      },
      validate: () => {
        return asyncValidation.socialID === undefined
          ? true
          : asyncValidation.socialID;
      }
    },
    dateOfBirth: {
      required: "Date of birth is required."
    },
    gender: {
      required: "Gender is required."
    },
    email: {
      required: "Email is required.",
      minLength: {
        value: userPolicy.email.minLength,
        message: `Email should have at least ${userPolicy.email.minLength} characters.`,
      },
      maxLength: {
        value: userPolicy.email.maxLength,
        message: `Email can't exceed ${userPolicy.email.maxLength} characters.`,
      },
      pattern: {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email address.",
      },
      valueExists: {
        message: "Email “{value}” has already signed up.\nPlease sign in if it is yours or use a different email."
      },
      validate: () => {
        return asyncValidation.email === undefined
          ? true
          : asyncValidation.email;
      }
    },
    mobile: {
      minLength: {
        value: userPolicy.mobile.minLength,
        message: `Mobile number should have at least ${userPolicy.mobile.minLength} numbers.`,
      },
      maxLength: {
        value: userPolicy.mobile.maxLength,
        message: `Mobile number can't exceed ${userPolicy.mobile.maxLength} numbers.`,
      },
      pattern: {
        value: /^[0-9+ ]*$/,
        message: "Invalid characters entered.",
      }
    },
    organization: {
      maxLength: {
        value: userPolicy.organization.maxLength,
        message: `Organization name can't exceed ${userPolicy.organization.maxLength} characters.`,
      }
    },
    designation: {
      maxLength: {
        value: userPolicy.designation.maxLength,
        message: `Designation can't exceed ${userPolicy.designation.maxLength} characters.`,
      }
    },
    institution: {
      maxLength: {
        value: userPolicy.institution.maxLength,
        message: `Institution name can't exceed ${userPolicy.institution.maxLength} characters.`,
      }
    },
    discipline: {
      maxLength: {
        value: userPolicy.discipline.maxLength,
        message: `Discipline can't exceed ${userPolicy.discipline.maxLength} characters.`,
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const {
    ref: firstNameRegisterRef,
    onBlur: firstNameRegisterOnBlur,
    ...firstNameRegister
  } = register("firstName", validations.firstName);

  const {
    ref: lastNameRegisterRef,
    onBlur: lastNameRegisterOnBlur,
    ...lastNameRegister
  } = register("lastName", validations.lastName);

  const {
    ref: socialIDRegisterRef,
    onChange: socialIDRegisterOnChange,
    onBlur: socialIDRegisterOnBlur,
    ...socialIDRegister
  } = register("socialID", _.omit(validations.socialID, "valueExists"));

  const {
    ref: dobRegisterRef,
    onChange: dobRegisterOnChange,
    onBlur: dobRegisterOnBlur,
    ...dobRegister
  } = register("dateOfBirth", validations.dateOfBirth);

  const {
    ref: genderRegisterRef,
    onChange: genderRegisterOnChange,
    onBlur: genderRegisterOnBlur,
    ...genderRegister
  } = register("gender", validations.gender);

  const {
    ref: emailRegisterRef,
    onChange: emailRegisterOnChange,
    onBlur: emailRegisterOnBlur,
    ...emailRegister
  } = register("email", _.omit(validations.email, "valueExists"));

  const {
    ref: mobileRegisterRef,
    onBlur: mobileRegisterOnBlur,
    ...mobileRegister
  } = register("mobile", validations.mobile);

  const {
    ref: organizationRegisterRef,
    onBlur: organizationRegisterOnBlur,
    ...organizationRegister
  } = register("organization", validations.organization);

  const {
    ref: designationRegisterRef,
    onBlur: designationRegisterOnBlur,
    ...designationRegister
  } = register("designation", validations.designation);

  const {
    ref: institutionRegisterRef,
    onBlur: institutionRegisterOnBlur,
    ...institutionRegister
  } = register("institution", validations.institution);

  const {
    ref: disciplineRegisterRef,
    onBlur: disciplineRegisterOnBlur,
    ...disciplineRegister
  } = register("discipline", validations.discipline);
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    openDialog,
    closeDialog,
    setOnBeforeClose,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const checkIfExists = useMemo(() => _.throttle(async (field: keyof IUserProfile, value: string) => {
    if (!value || value.length < _.get(userPolicy, `${field}.minLength`)) {
      clearErrors(field);
      setAsyncValidation(prev => ({
        ...prev,
        [field]: true
      }));

    } else {
      try {
        const data = await $core.idExists({params: {id: value}});
        const isValid = !_.get(data, "idExists", true);

        if (isValid) {
          clearErrors(field);
          setAsyncValidation(prev => ({
            ...prev,
            [field]: true
          }));

        } else {
          setError(field, {
            type: "validate",
            message: validations[field].valueExists.message.replace("{value}", value)
          });

          setAsyncValidation(prev => ({
            ...prev,
            [field]: isValid
              ? true
              : validations[field].valueExists.message.replace("{value}", value)
          }));
        }

      } catch {
        setError(field, {
          type: "validate",
          message: `A network error occurred while checking if ${field} exists. Please try again.`
        });

        setAsyncValidation(prev => ({
          ...prev,
          [field]: `A network error occurred while checking if ${field} exists. Please try again.`
        }));
      }
    }

    const t = setTimeout(async () => {
      await trigger(field);
      clearTimeout(t);
    })
  }, 5, {
    leading: true,
    trailing: false
  }), [userPolicy]);

  const onPhotoClick = () => {
    suppress();
    openDialog(MediaPicker, {
      ...pages.signup.mediaPicker.properties,
      defaultImage: defaultImage,
      initialImage: photo || defaultImage,
      rounded: true,
      minSize: userPolicy.photo.minSize,
      maxSize: userPolicy.photo.maxSize,
      cropperProps: pages.signup.mediaPicker.cropperProps,
      onSave: onPhotoSelect,
      onClose: unsuppress
    });
  };

  const onPhotoSelect = (img: any) => {
    const photo = _.isBlob(img) ? URL.createObjectURL(img) : img;
    setImage(img);
    setPhoto(photo || defaultImage);

    const t = setTimeout(() => {
      firstNameRef?.current?.focus();
      clearTimeout(t);
    }, 10);
  };

  const onGenderChange = async (gender: GenderType) => {
    void setValue("gender", gender, {
      shouldValidate: false,
      shouldDirty: true
    });
    if (emailRef?.current) emailRef?.current?.focus();
  };

  const onGenderFocus = (e: any) => {
    e.preventDefault();
    setGenderClass("gender-radio gender-radio-focused");
  };

  const onGenderBlur = async () => {
    setGenderClass("gender-radio");
    await trigger("gender");
  };

  const onMobileChange = async (value: any) => {
    const country = value.country;
    void setValue("country", country, {shouldDirty: true});
    const mobile = value.number;
    void setValue("mobile", mobile, {
      shouldValidate: false,
      shouldDirty: true
    });
  };

  const onMobileBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value.length < 1) return;
    const dialCode = e.target.value.replace(/^\+(\d+).*/, "$1");
    const mobile = e.target.value.replace(/^\+\d+\s*/, ``).replace(/\s/g, ``)
    if (dialCode === defaults.country?.dialCode && mobile === defaults.mobile) return;
    await mobileRegisterOnBlur(e);
    await trigger("mobile");
  };

  const onReset = () => {
    setImage(null);
    setPhoto(defaultImage);
    setResetKey((prev) => prev + 1);
    reset();
    const t = setTimeout(() => {
      firstNameRef?.current?.focus();
      clearTimeout(t);
    }, 10);
  };

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSubmit();
  };

  const _onSubmit = async () => {
    if (!isValid) return;
    suppress();
    await handleSubmit(onSubmit)();
  };

  const onSubmit = async () => {
    openDialog(Confirm, {
      ...pages.signup.consent.properties,
      title: pages.signup.consent.title,
      text: pages.signup.consent.text,
      yesLabel: "Proceed",
      noLabel: "Cancel",
      onYes: async () => {
        await signup();
        unsuppress();
      },

      onNo: async () => {
        if (lastFocusedRef?.current) {
          if (lastFocusedRef.current === "mobile") {
            mobileRef.current?.focus();
          } else {
            setFocus(lastFocusedRef.current);
          }
        }
        unsuppress();
      }
    });
  };

  const signup = async () => {
    setLoading(true);
    const values = _.omitBy(getValues(), (value) => _.isNil(value) || _.isNothing(value));

    const payload: IUserPayload = _.omitBy({
      ...values,
      photo: image || null,
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as IUserPayload;

    await $core.signup(payload).then(async (data: IUserSignupResponse) => {
      $store.User.user$.next(data);
      setLoading(false);
      await closeDialog(true);
      onSuccess?.();

    }).catch((e: any) => {
      setLoading(false);
      if (e?.code === HttpStatus.BAD_REQUEST && _.isArray(e?.data) && e?.data?.length > 0) {
        const fieldErrors = e?.data?.filter((f: any) => ["invalidData", "validationError"].indexOf(f?.error) < 0);
        fieldErrors.forEach((f: any) => {
          const fieldName = f?.attribute as keyof IUserProfile;
          const message = _.get(validations, `${fieldName}.${f?.error === "required" ? "required" : `${f?.error}.message`}`, null)
            .replace("{value}", getValues(fieldName));

          if (fieldName && message) {
            setError(fieldName, {
              type: "validate",
              message
            });
          }
        });
        _.toastError(`Signup form contains invalid data.\nPlease correct highlighted fields and submit again.`);

      } else {
        _.toastError(_.get(e, 'message'));
      }
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    const SHOW_THRESHOLD = 5;
    const HIDE_THRESHOLD = 25;
    const TOP_HIDE_THRESHOLD = 40;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const _onBeforeClose = useCallback(() => {
    if (!isUnsaved) return true;

    return new Promise<boolean>((resolve) => {
      suppress();

      openDialog(Confirm, {
        ...pages.confirm.properties,
        text: "Abort Signup?",

        onYes: async () => {
          unsuppress();
          await closeDialog(true);
          resolve(true);
        },

        onNo: async () => {
          unsuppress();
          if (lastFocusedRef?.current) {
            if (lastFocusedRef.current === "mobile") {
              mobileRef.current?.focus();
            } else {
              setFocus(lastFocusedRef.current);
            }
          }
          resolve(false);
        }
      });
    });
  }, [isUnsaved]);

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setDefaultImage(defaultProfilePic);
    firstNameRef?.current?.focus();
  }, []);

  useEffect(() => {
    setIsUnsaved(isDirty || (photo !== defaultProfilePic));
  }, [isDirty, photo]);

  useEffect(() => {
    setOnBeforeClose(_onBeforeClose);
  }, [setOnBeforeClose, _onBeforeClose]);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: pages.signup.title,
      style: "text-sm sm:text-md md:text-lg font-bold text-white tracking-tight md:tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !isUnsaved,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "signup",
      type: "gradientButton",
      action: "submit",
      label: "Sign up",
      style: `btn-signup btn-${isValid && isUnsaved ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: !isValid,
      onClick: () => _onSubmit(),
      onEnter: () => _onSubmit()
    },
    {
      name: "cancel",
      type: "gradientButton",
      label: "Cancel",
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: () => closeDialog(),
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute"
      borderRadius="rounded-sm md:rounded-md"
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const signupForm = (
    <form
      onSubmit={_onSubmit}
      className={`form-${className} grid px-4 gap-6 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>

      {/* Row 1 – General photo + name */}
      <div
        className="w-full h-[6.8rem] flex flex-col items-center text-center">
        <div
          className="profile-picture w-fit h-fit rounded-full overflow-hidden shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]! hover:shadow-[1px_1px_2px_1px_rgba(55,90,105,1)]!">
          <img
            src={photo ?? defaultImage}
            alt="General photo"
            className="w-20 h-20 object-contain rounded-full border-none cursor-pointer"
            onClick={onPhotoClick}
          />
        </div>
        <div
          className="font-semibold text-sm text-black dark:text-ghost-white tracking-wider mt-2">
          {firstName || lastName ? `${firstName} ${lastName}` : ""}
        </div>
      </div>

      {/* Row 2 – First + Last */}
      <RowGrid>
        <InputValidator
          ref={useMergeRefs(firstNameRef, firstNameRegisterRef)}
          placeholder="First Name*"
          minLength={userPolicy.name.minLength}
          maxLength={userPolicy.name.maxLength}
          mask={/[a-zA-Z ]/}
          format="properCase"
          value={watch("firstName")}
          error={errors.firstName?.message}
          {...firstNameRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = firstNameRegister.name)}
          onBlur={async (e) => {
            await firstNameRegisterOnBlur(e);
            await trigger("firstName");
          }}
        />
        <InputValidator
          ref={lastNameRegisterRef}
          placeholder="Last Name*"
          minLength={userPolicy.name.minLength}
          maxLength={userPolicy.name.maxLength}
          mask={/[a-zA-Z ]/}
          format="properCase"
          value={watch("lastName")}
          error={errors.lastName?.message}
          {...lastNameRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = lastNameRegister.name)}
          onBlur={async (e) => {
            await lastNameRegisterOnBlur(e);
            await trigger("lastName");
          }}
        />
      </RowGrid>

      {/* Row 3 – SocialID + Gender + DOB */}
      <RowGrid>
        <InputValidator
          ref={useMergeRefs(socialIDRef, socialIDRegisterRef)}
          placeholder="Social ID*"
          minLength={userPolicy.socialID.minLength}
          maxLength={userPolicy.socialID.maxLength}
          mask={/[a-zA-Z0-9-@_.]/}
          value={watch("socialID")}
          error={
            asyncValidation.socialID !== true
              ? asyncValidation.socialID
              : errors.socialID?.type === "validate"
                ? errors.socialID?.message
                : undefined
          }
          {...socialIDRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = socialIDRegister.name)}
          onChange={async (e) => {
            await socialIDRegisterOnChange(e);
            void checkIfExists("socialID", e.target.value);
          }}
          onBlur={async (e) => {
            await socialIDRegisterOnBlur(e);
            await trigger("socialID");
          }}
        />
        <div
          className="flex flex-row gap-2 p-0!">
          <DatePicker
            key={`d-${resetKey}`}
            ref={useMergeRefs(dobRef, dobRegisterRef)}
            placeholder="Date of Birth*"
            minAge={18}
            error={errors.dateOfBirth?.message}
            {...dobRegister}
            onFocus={() => (lastFocusedRef.current = dobRegister.name)}
            onDateChange={(date: Date) => {
              void setValue("dateOfBirth", date.toISOString(), {
                shouldValidate: true,
                shouldDirty: true
              });
            }}
            onClose={async () => {
              await trigger("dateOfBirth");
            }}
          />
          <GenderRadio
            key={`g-${resetKey}`}
            ref={useMergeRefs(genderRef, genderRegisterRef)}
            placeholder="Gender*"
            className={genderClass}
            error={errors.gender?.message}
            {...genderRegister}
            onGenderChange={onGenderChange}
            onFocus={(e) => {
              lastFocusedRef.current = genderRegister.name;
              onGenderFocus(e);
            }}
            onBlur={async (e) => {
              await genderRegisterOnBlur(e);
              await onGenderBlur?.();
            }}
            onButtonBlur={async () => {
              await onGenderBlur?.();
            }}
          />
        </div>
      </RowGrid>

      {/* Row 4 – Email + Mobile */}
      <RowGrid>
        <InputValidator
          ref={useMergeRefs(emailRef, emailRegisterRef)}
          placeholder="Email*"
          readOnly={!isUser}
          minLength={userPolicy.email.minLength}
          maxLength={userPolicy.email.maxLength}
          mask={/[a-zA-Z0-9@._%+-]/}
          format="email"
          value={watch("email")}
          error={
            asyncValidation.email !== true
              ? asyncValidation.email
              : errors.email?.type === "validate"
                ? errors.email?.message
                : undefined
          }
          {...emailRegister}
          showToast={_.toastError}
          onFocus={() => (lastFocusedRef.current = emailRegister.name)}
          onChange={async (e) => {
            await emailRegisterOnChange(e);
            void checkIfExists("email", e.target.value);
          }}
          onBlur={async (e) => {
            await emailRegisterOnBlur(e);
            await trigger("email");
          }}
        />
        <PhoneInput
          key={`p-${resetKey}`}
          parentRef={mobileRegisterRef}
          inputRef={mobileRef}
          placeholder="Mobile"
          minLength={userPolicy.mobile.minLength}
          maxLength={userPolicy.mobile.maxLength}
          mask={/[0-9]/}
          format="phone"
          countries={countries}
          country={country}
          error={errors.mobile?.message}
          name={mobileRegister.name}
          showToast={_.toastError}
          onChange={onMobileChange}
          onFocus={() => (lastFocusedRef.current = mobileRegister.name)}
          onBlur={onMobileBlur}
        />
      </RowGrid>

      {/* Row 5 – (Organization / Designation) or (Institution / Discipline) */}
      {!isStudent ? (
        <RowGrid>
          <InputValidator
            ref={organizationRegisterRef}
            placeholder="Organization"
            readOnly={isAdmin || isBizAdmin || isStaff}
            maxLength={userPolicy.organization.maxLength}
            mask={/[a-zA-Z0-9@. ]/}
            value={watch("organization")}
            error={errors.organization?.message}
            {...organizationRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = organizationRegister.name)}
          />
          <InputValidator
            ref={designationRegisterRef}
            placeholder="Designation"
            maxLength={userPolicy.designation.maxLength}
            mask={/[a-zA-Z0-9@ .()-]/}
            value={watch("designation")}
            error={errors.designation?.message}
            {...designationRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = designationRegister.name)}
          />
        </RowGrid>
      ) : (
        <RowGrid>
          <InputValidator
            ref={institutionRegisterRef}
            placeholder="Institution"
            maxLength={userPolicy.institution.maxLength}
            mask={/[a-zA-Z0-9@. ]/}
            value={watch("institution")}
            error={errors.institution?.message}
            {...institutionRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = institutionRegister.name)}
          />
          <InputValidator
            ref={disciplineRegisterRef}
            placeholder="Discipline"
            maxLength={userPolicy.discipline.maxLength}
            mask={/[a-zA-Z0-9@ .()-]/}
            value={watch("discipline")}
            error={errors.discipline?.message}
            {...disciplineRegister}
            showToast={_.toastError}
            onFocus={() => (lastFocusedRef.current = disciplineRegister.name)}
          />
        </RowGrid>
      )}
    </form>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
          ${isOverflow ? "pt-4" : "py-4"}
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      {signupForm}
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
          absolute bottom-0 left-0 right-0 z-50
          transition-transform duration-300 ease-in-out
          ${showFooter ? "translate-y-0" : "translate-y-full"}
        `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
